import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-endpoints';
import authService from '../services/authService';

export interface ClassAssignment {
  id: number;
  class_id: number;
  title: string;
  description: string;
  assignment_type: string;
  due_date: string;
  max_score: number;
  attachment_url: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface AssignmentSubmissionData {
  assignment_id: number;
  user_id: number;
  submission_text: string;
  submission_url?: string;
  status: string;
}

export interface UpdateAssignmentSubmissionData {
  submission_text: string;
  submission_url?: string;
  status: string;
}

export interface UserSubmission {
  id: number;
  assignment_id: number;
  user_id: number;
  submission_text: string;
  submission_url: string;
  score: number;
  feedback: string;
  status: string;
  submitted_at: string;
  graded_at: string | null;
  graded_by: number | null;
}

// Hook to get class assignments by ClassId
export function useClassAssignments(classId: string | number) {
  return useQuery({
    queryKey: ['classAssignments', classId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.GET_CLASS_ASSIGNMENTS(classId.toString())}`;
      
      console.log('Fetching class assignments for class ID:', classId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('Class Assignments API Response:', res);

      if (res.error) {
        console.error('Class assignments error:', res.error);
        throw new Error('Failed to fetch class assignments');
      }
      
      // Return the data array
      const assignmentsData = res.data || res;
      console.log('Processed Class Assignments Data:', assignmentsData);
      return Array.isArray(assignmentsData) ? assignmentsData : [assignmentsData] as ClassAssignment[];
    },
    enabled: !!classId,
  });
}

// Hook to get user submission by assignment ID and user ID
export function useUserSubmission(assignmentId: string | number, userId: string | number) {
  return useQuery({
    queryKey: ['userSubmission', assignmentId, userId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.GET_USER_SUBMISSION(assignmentId.toString(), userId.toString())}`;
      
      console.log('Fetching user submission for assignment ID:', assignmentId, 'user ID:', userId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('User Submission API Response:', res);

      if (res.error) {
        console.error('User submission error:', res.error);
        return null; // Return null if no submission found
      }
      
      // Return the data
      const submissionData = res.data || res;
      console.log('Processed User Submission Data:', submissionData);
      return submissionData as UserSubmission;
    },
    enabled: !!(assignmentId && userId),
    retry: false, // Don't retry if no submission found
  });
}

// Hook to create assignment submission
export function useCreateAssignmentSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (submissionData: AssignmentSubmissionData) => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.CREATE_ASSIGNMENT_SUBMISSION}`;
      
      console.log('Creating assignment submission:', submissionData);
      console.log('Full URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();
      
      console.log('Assignment Submission API Result:', result);
      
      if (!response.ok || result.error) {
        throw new Error(result.message || 'Failed to create assignment submission');
      }
      
      return result.data || result;
    },
    onSuccess: () => {
      // Invalidate and refetch assignments and submissions
      queryClient.invalidateQueries({ queryKey: ['classAssignments'] });
      queryClient.invalidateQueries({ queryKey: ['userSubmission'] });
    },
  });
}

// Hook to update assignment submission
export function useUpdateAssignmentSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ submissionId, data }: { submissionId: number; data: UpdateAssignmentSubmissionData }) => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const url = `${baseURL}${API_ENDPOINTS.UPDATE_ASSIGNMENT_SUBMISSION(submissionId.toString())}`;
      
      console.log('Updating assignment submission:', submissionId, data);
      console.log('Full URL:', url);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      
      console.log('Update Assignment Submission API Result:', result);
      
      if (!response.ok || result.error) {
        throw new Error(result.message || 'Failed to update assignment submission');
      }
      
      return result.data || result;
    },
    onSuccess: () => {
      // Invalidate and refetch assignments and submissions
      queryClient.invalidateQueries({ queryKey: ['classAssignments'] });
      queryClient.invalidateQueries({ queryKey: ['userSubmission'] });
    },
  });
}
